import { parseFish, type Fish } from '../components/april';
import { isLocale, type Locale } from '../i18n/strings';
import { randomInt } from '../lib/random';
import { MAX_KIKOO_LENGTH, sanitizeKikoo } from '../lib/sanitize';
import { readInt, readString, writeString } from '../lib/storage';
import {
  MAX_SEEN,
  SKINS,
  isSkin,
  isSkyblogTheme,
  type Skin,
  type SkyblogTheme,
} from './types';

/** Clé du compteur du jour : un seau par date locale (cf. handoff). */
export function seenKey(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `steve.seen.${String(year)}-${month}-${day}`;
}

/** Clé du thème skyblog. */
export const SB_THEME_KEY = 'steve.sbTheme';

/** Clé du pseudo kikoo adopté. */
export const KIKOO_KEY = 'steve.kikoo';

/**
 * Pseudo mémorisé, ou rien du tout — auquel cas le visiteur signe
 * `visiteur_anonyme`.
 *
 * Comme le thème, la valeur est re-nettoyée à la relecture : une clé trafiquée
 * ne peut pas glisser un caractère de contrôle dans une signature qui s'affiche
 * sur les onze interfaces.
 */
export function resolveKikoo(): string {
  return sanitizeKikoo(readString(KIKOO_KEY, MAX_KIKOO_LENGTH));
}

/** Clé des poissons en papier collés sur l'interface. */
export const APRIL_KEY = 'steve.april';

/**
 * Longueur maximale de la valeur mémorisée.
 *
 * Onze découpes de cinq entiers font moins de 200 caractères ; au-delà, la
 * valeur n'est pas la nôtre et `readString` la refuse en bloc.
 */
const APRIL_MAX_LENGTH = 256;

/** Les poissons collés lors d'une visite précédente, re-validés un par un. */
export function resolveFish(): readonly Fish[] {
  return parseFish(readString(APRIL_KEY, APRIL_MAX_LENGTH));
}

/**
 * Thème du skyblog mémorisé, ou le rose bonbon par défaut.
 *
 * Comme tout ce qui sort du stockage, la valeur est re-validée : une clé
 * trafiquée ne peut pas produire un nom de thème inconnu qui laisserait la page
 * sans couleurs.
 */
export function resolveSkyblogTheme(): SkyblogTheme {
  const stored = readString(SB_THEME_KEY, 16);
  return isSkyblogTheme(stored) ? stored : 'pink';
}

function searchParams(search: string): URLSearchParams {
  try {
    return new URLSearchParams(search);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Skin : tirage au sort à chaque chargement, forçable par `?skin=`.
 * Le paramètre n'est lu qu'au montage — le skin ne change plus ensuite, sauf
 * action explicite du sélecteur.
 */
export function resolveSkin(search: string): Skin {
  const forced = searchParams(search).get('skin');
  if (isSkin(forced)) return forced;
  return SKINS[randomInt(SKINS.length)] ?? 'aqua';
}

/** Tire un skin différent de l'actuel (mot secret, « aléatoire » du webring). */
export function rerollSkin(current: Skin): Skin {
  const others = SKINS.filter((skin) => skin !== current);
  return others[randomInt(others.length)] ?? current;
}

/**
 * Skin voisin dans l'anneau — les trois interfaces forment le webring, les
 * flèches « précédent » et « suivant » y tournent en rond.
 */
export function stepSkin(current: Skin, step: number): Skin {
  const index = SKINS.indexOf(current);
  if (index < 0) return current;
  const size = SKINS.length;
  return SKINS[(((index + step) % size) + size) % size] ?? current;
}

/**
 * Langue : celle du navigateur, forçable par `?lang=` — ce dernier n'est pas
 * exposé dans l'interface, il sert aux tests et au partage d'un lien dans une
 * langue précise.
 *
 * Rien n'est mémorisé : il n'y a plus de sélecteur, une préférence enregistrée
 * ne pourrait jamais être révisée par le visiteur.
 *
 * Le français est le repli, c'est la langue source du meme.
 */
export function resolveLang(search: string, navigatorLanguage: string | undefined): Locale {
  const forced = searchParams(search).get('lang');
  if (isLocale(forced)) return forced;

  return navigatorLanguage?.toLowerCase().startsWith('en') ? 'en' : 'fr';
}

/**
 * Compteur du jour : lu, incrémenté d'une unité, réécrit. Si le stockage est
 * indisponible on renvoie 1 — le visiteur est toujours le premier poisson vu.
 */
export function bumpSeenForToday(now: Date = new Date()): number {
  const key = seenKey(now);
  const next = Math.min(MAX_SEEN, readInt(key, 0, MAX_SEEN) + 1);
  writeString(key, String(next));
  return next;
}
